import axios from "axios";
import { adminToken } from "./api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const STUDIO_API = `${BACKEND_URL}/api/studio`;

const STUDIO_TOKEN_KEY = "pv_studio_token";
const STUDIO_USER_KEY = "pv_studio_user";

export const studioAuth = {
  getToken: () => localStorage.getItem(STUDIO_TOKEN_KEY) || "",
  setToken: (t) => localStorage.setItem(STUDIO_TOKEN_KEY, t),
  clear: () => {
    localStorage.removeItem(STUDIO_TOKEN_KEY);
    localStorage.removeItem(STUDIO_USER_KEY);
  },
  getUser: () => {
    try {
      return JSON.parse(localStorage.getItem(STUDIO_USER_KEY) || "null");
    } catch {
      return null;
    }
  },
  setUser: (u) => localStorage.setItem(STUDIO_USER_KEY, JSON.stringify(u)),
};

function userHeaders() {
  return { Authorization: `Bearer ${studioAuth.getToken()}` };
}
function adminHeaders() {
  return { Authorization: `Bearer ${adminToken.get()}` };
}

// ---------- Safe API error formatter ----------
// When an upstream proxy (Cloudflare, an ingress controller, etc.) returns a
// 5xx response, the body is typically a raw HTML error page — NOT JSON. Naive
// code that does `err.response.data.detail || "Fallback"` is safe in that case
// because string?.detail is undefined, but `err.response.data` itself can be
// a long HTML string and downstream code that reads it directly will dump the
// whole page into a toast. This helper guarantees we only ever render a short,
// plain-text, non-HTML message — falling back to the supplied generic copy
// whenever the response body looks like HTML, isn't a string, or is too long.
export function formatApiError(err, fallback = "Something went wrong. Please try again.") {
  const data = err?.response?.data;
  const candidate =
    (data && typeof data === "object" && typeof data.detail === "string" && data.detail) ||
    (typeof data === "string" && data) ||
    "";
  const trimmed = candidate.trim();
  if (
    !trimmed ||
    trimmed.length > 200 ||
    /<\s*(html|body|head|!doctype|h\d|p\b|div|span|script|style)/i.test(trimmed)
  ) {
    return fallback;
  }
  return trimmed;
}

// ---------- Studio user (artist) endpoints ----------
export async function studioLogin(email, password) {
  const { data } = await axios.post(`${STUDIO_API}/auth/login`, { email, password });
  studioAuth.setToken(data.token);
  studioAuth.setUser(data.user);
  return data;
}
export async function fetchMe() {
  const { data } = await axios.get(`${STUDIO_API}/me`, { headers: userHeaders() });
  return data;
}
export async function fetchMyPosts() {
  const { data } = await axios.get(`${STUDIO_API}/posts`, { headers: userHeaders() });
  return data;
}

// ---------- Admin endpoints (use ADMIN_SECRET) ----------
export async function adminListUsers() {
  const { data } = await axios.get(`${STUDIO_API}/admin/users`, { headers: adminHeaders() });
  return data;
}
export async function adminCreateUser(payload) {
  const { data } = await axios.post(`${STUDIO_API}/admin/users`, payload, { headers: adminHeaders() });
  return data;
}
export async function adminUpdateUser(id, payload) {
  const { data } = await axios.patch(`${STUDIO_API}/admin/users/${id}`, payload, { headers: adminHeaders() });
  return data;
}
export async function adminDeleteUser(id) {
  const { data } = await axios.delete(`${STUDIO_API}/admin/users/${id}`, { headers: adminHeaders() });
  return data;
}
export async function adminGetStatuses() {
  const { data } = await axios.get(`${STUDIO_API}/admin/statuses`, { headers: adminHeaders() });
  return data;
}
export async function adminGetMembershipSources() {
  const { data } = await axios.get(`${STUDIO_API}/admin/membership-sources`, {
    headers: adminHeaders(),
  });
  return data;
}
export async function adminListUserPosts(userId) {
  const { data } = await axios.get(`${STUDIO_API}/admin/posts/${userId}`, { headers: adminHeaders() });
  return data;
}
export async function adminListGlobalPosts() {
  const { data } = await axios.get(`${STUDIO_API}/admin/posts`, { headers: adminHeaders() });
  return data;
}
export async function adminCreatePost(payload) {
  const { data } = await axios.post(`${STUDIO_API}/admin/posts`, payload, { headers: adminHeaders() });
  return data;
}
export async function adminDeletePost(postId) {
  const { data } = await axios.delete(`${STUDIO_API}/admin/posts/${postId}`, { headers: adminHeaders() });
  return data;
}

// ---------- Artist-authored posts ----------
export async function createMyPost(payload) {
  // payload: { title?, body?, attachments?[] } — server forces userId to current user
  const { data } = await axios.post(`${STUDIO_API}/posts`, payload, { headers: userHeaders() });
  return data;
}
export async function deleteMyPost(postId) {
  const { data } = await axios.delete(`${STUDIO_API}/posts/${postId}`, { headers: userHeaders() });
  return data;
}

// ---------- Direct Messages ----------
export async function fetchMyMessages() {
  const { data } = await axios.get(`${STUDIO_API}/messages`, { headers: userHeaders() });
  return data;
}
export async function sendMyMessage(body, attachments = []) {
  const { data } = await axios.post(`${STUDIO_API}/messages`, { body, attachments }, { headers: userHeaders() });
  return data;
}
export async function fetchMyUnreadCount() {
  const { data } = await axios.get(`${STUDIO_API}/messages/unread-count`, { headers: userHeaders() });
  return data.unread;
}
export async function adminListThreads() {
  const { data } = await axios.get(`${STUDIO_API}/admin/threads`, { headers: adminHeaders() });
  return data;
}
export async function adminGetThread(userId) {
  const { data } = await axios.get(`${STUDIO_API}/admin/messages/${userId}`, { headers: adminHeaders() });
  return data;
}
export async function adminSendMessage(userId, body, attachments = []) {
  const { data } = await axios.post(`${STUDIO_API}/admin/messages/${userId}`, { body, attachments }, { headers: adminHeaders() });
  return data;
}
export async function adminMessagesUnreadCount() {
  const { data } = await axios.get(`${STUDIO_API}/admin/messages-unread-count`, { headers: adminHeaders() });
  return data.unread;
}

// ---------- Reactions ----------
export async function userReactToMessage(messageId, emoji) {
  const { data } = await axios.post(`${STUDIO_API}/messages/${messageId}/react`, { emoji }, { headers: userHeaders() });
  return data;
}
export async function adminReactToMessage(messageId, emoji) {
  const { data } = await axios.post(`${STUDIO_API}/admin/messages/${messageId}/react`, { emoji }, { headers: adminHeaders() });
  return data;
}

// ---------- Typing indicators ----------
export async function pingUserTyping() {
  await axios.post(`${STUDIO_API}/typing`, {}, { headers: userHeaders() });
}
export async function fetchUserTypingStatus() {
  const { data } = await axios.get(`${STUDIO_API}/typing`, { headers: userHeaders() });
  return data.adminTyping;
}
export async function pingAdminTyping(userId) {
  await axios.post(`${STUDIO_API}/admin/typing/${userId}`, {}, { headers: adminHeaders() });
}
export async function fetchAdminTypingStatus(userId) {
  const { data } = await axios.get(`${STUDIO_API}/admin/typing/${userId}`, { headers: adminHeaders() });
  return data.userTyping;
}

// ---------- Notifications (bell digest) ----------
export async function fetchNotifications() {
  const { data } = await axios.get(`${STUDIO_API}/notifications`, { headers: userHeaders() });
  return data;
}
export async function markNotificationsRead() {
  const { data } = await axios.post(`${STUDIO_API}/notifications/mark-read`, {}, { headers: userHeaders() });
  return data;
}

// ---------- Live sessions (Go Live / Google Meet) ----------
export async function adminStartLive(payload) {
  // payload = { title, meetUrl, scope: 'global' | 'user', userId?, sendEmail? }
  const { data } = await axios.post(`${STUDIO_API}/admin/live`, payload, { headers: adminHeaders() });
  return data;
}
export async function adminEndLive(sessionId) {
  const { data } = await axios.post(`${STUDIO_API}/admin/live/${sessionId}/end`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminFetchActiveLive() {
  const { data } = await axios.get(`${STUDIO_API}/admin/live/active`, { headers: adminHeaders() });
  return data.session;
}
export async function fetchMyActiveLive() {
  const { data } = await axios.get(`${STUDIO_API}/live/active`, { headers: userHeaders() });
  return data.session;
}

// ---------- Billing & subscriptions ----------
export async function fetchPlans() {
  const { data } = await axios.get(`${STUDIO_API}/billing/plans`);
  return data;
}
export async function createCheckoutSession(planKey, originUrl) {
  const { data } = await axios.post(`${STUDIO_API}/billing/checkout`, { planKey, originUrl });
  return data;
}
export async function fetchCheckoutStatus(sessionId) {
  const { data } = await axios.get(`${STUDIO_API}/billing/checkout/status/${sessionId}`);
  return data;
}
export async function finishSignup({ sessionId, name, email, password, artistName }) {
  const { data } = await axios.post(`${STUDIO_API}/billing/finish-signup`, {
    sessionId, name, email, password, artistName,
  });
  return data;
}
export async function fetchMySubscription() {
  const { data } = await axios.get(`${STUDIO_API}/billing/me`, { headers: userHeaders() });
  return data;
}
export async function fetchMyPayments() {
  const { data } = await axios.get(`${STUDIO_API}/billing/payments`, { headers: userHeaders() });
  return data;
}
export async function cancelMySubscription() {
  const { data } = await axios.post(`${STUDIO_API}/billing/cancel`, {}, { headers: userHeaders() });
  return data;
}
export async function reactivateMySubscription() {
  const { data } = await axios.post(`${STUDIO_API}/billing/reactivate`, {}, { headers: userHeaders() });
  return data;
}
export async function openCustomerPortal() {
  const { data } = await axios.post(`${STUDIO_API}/billing/portal`, {}, { headers: userHeaders() });
  return data.url;
}

// ---------- Perks library ----------
export async function fetchPerkCategories({ asAdmin = false } = {}) {
  const headers = asAdmin ? adminHeaders() : userHeaders();
  const { data } = await axios.get(`${STUDIO_API}/perks/categories`, { headers });
  return data;
}
export async function adminCreatePerkCategory(payload) {
  const { data } = await axios.post(`${STUDIO_API}/admin/perks/categories`, payload, { headers: adminHeaders() });
  return data;
}
export async function adminUpdatePerkCategory(id, payload) {
  const { data } = await axios.patch(`${STUDIO_API}/admin/perks/categories/${id}`, payload, { headers: adminHeaders() });
  return data;
}
export async function adminDeletePerkCategory(id) {
  const { data } = await axios.delete(`${STUDIO_API}/admin/perks/categories/${id}`, { headers: adminHeaders() });
  return data;
}
export async function adminListPerks() {
  const { data } = await axios.get(`${STUDIO_API}/admin/perks`, { headers: adminHeaders() });
  return data;
}
export async function adminCreatePerk(payload) {
  const { data } = await axios.post(`${STUDIO_API}/admin/perks`, payload, { headers: adminHeaders() });
  return data;
}
export async function adminUpdatePerk(id, payload) {
  const { data } = await axios.patch(`${STUDIO_API}/admin/perks/${id}`, payload, { headers: adminHeaders() });
  return data;
}
export async function adminDeletePerk(id) {
  const { data } = await axios.delete(`${STUDIO_API}/admin/perks/${id}`, { headers: adminHeaders() });
  return data;
}
export async function fetchMyPerks() {
  const { data } = await axios.get(`${STUDIO_API}/perks`, { headers: userHeaders() });
  return data;
}
export async function fetchPerksUnreadCount() {
  const { data } = await axios.get(`${STUDIO_API}/perks/unread-count`, { headers: userHeaders() });
  return data.unread || 0;
}
export async function markPerksRead() {
  const { data } = await axios.post(`${STUDIO_API}/perks/mark-read`, {}, { headers: userHeaders() });
  return data;
}

// ---------- Uploads (R2) ----------
// Works for both admin and authenticated studio user — passes whichever token is present.
export async function signUpload({ filename, contentType, kind = "file", size }, { asAdmin = false } = {}) {
  const headers = asAdmin ? adminHeaders() : userHeaders();
  const { data } = await axios.post(
    `${STUDIO_API}/uploads/sign`,
    { filename, contentType, kind, size },
    { headers }
  );
  return data; // { uploadUrl, publicUrl, key }
}

export async function uploadToR2(file, kind = "file", { asAdmin = false, onProgress } = {}) {
  const { uploadUrl, publicUrl } = await signUpload(
    { filename: file.name, contentType: file.type || "application/octet-stream", kind, size: file.size },
    { asAdmin }
  );
  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": file.type || "application/octet-stream" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return { url: publicUrl, name: file.name, size: file.size, contentType: file.type };
}

// ---------- Profile / Settings / Heartbeat / Peer ----------
export async function updateMe(payload) {
  // payload = { artistName?, password? }
  const { data } = await axios.patch(`${STUDIO_API}/me`, payload, { headers: userHeaders() });
  studioAuth.setUser(data);
  return data;
}
export async function uploadMyAvatar(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${STUDIO_API}/me/avatar`, form, {
    headers: { ...userHeaders(), "Content-Type": "multipart/form-data" },
  });
  studioAuth.setUser(data);
  return data;
}
export async function pingUserHeartbeat() {
  await axios.post(`${STUDIO_API}/heartbeat`, {}, { headers: userHeaders() });
}
export async function fetchStudioPeer() {
  const { data } = await axios.get(`${STUDIO_API}/peer`, { headers: userHeaders() });
  return data;
}

// ---------- Admin profile ----------
export async function adminFetchMe() {
  const { data } = await axios.get(`${STUDIO_API}/admin/me`, { headers: adminHeaders() });
  return data;
}
export async function adminUpdateMe(payload) {
  const { data } = await axios.patch(`${STUDIO_API}/admin/me`, payload, { headers: adminHeaders() });
  return data;
}
export async function adminUploadAvatar(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${STUDIO_API}/admin/me/avatar`, form, {
    headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
  });
  return data;
}
export async function pingAdminHeartbeat() {
  await axios.post(`${STUDIO_API}/admin/heartbeat`, {}, { headers: adminHeaders() });
}
